import { useState, useEffect } from 'react';
import './TopPanel.css';
import { Link } from 'react-router-dom';
import UserAvatar from './UserAvatar';
import CreateCommunityEventPanel from './CreateCommunityEventPanel';
import { eventsAPI, communityEventsAPI, usersAPI } from './services/api';
import { parseUTCDate } from './utils/dateUtils';
import { useTranslation } from 'react-i18next';

function TopPanel({ currentUser }) {
  const { t, i18n } = useTranslation();
const [displayUser, setDisplayUser] = useState(currentUser);
  const [publicActivityArtCount, setPublicActivityArtCount] = useState(0);
  const [publicActivitySportCount, setPublicActivitySportCount] = useState(0);
  const [publicActivityArtLastMonth, setPublicActivityArtLastMonth] = useState(0);
  const [publicActivitySportLastMonth, setPublicActivitySportLastMonth] = useState(0);
  // const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  // const [pendingRefreshTrigger, setPendingRefreshTrigger] = useState(0);
  const [showCreateCommunityEvent, setShowCreateCommunityEvent] = useState(false);
  const [hasBlockingPendingEvent, setHasBlockingPendingEvent] = useState(false);

  // Update displayUser whenever currentUser prop changes
  // Always use currentUser from props as the source of truth on login
  useEffect(() => {
    if (currentUser) {
      setDisplayUser(currentUser);
    } else {
      setDisplayUser(null);
    }
  }, [currentUser]);

  // Check if user has a recent unapproved gathering (blocks creating new ones)
  const refreshBlockingPendingEvent = () => {
    if (!displayUser) return;
    communityEventsAPI.hasPendingUnapproved(displayUser.id)
      .then(setHasBlockingPendingEvent)
      .catch(() => setHasBlockingPendingEvent(false));
  };

  useEffect(() => {
    refreshBlockingPendingEvent();
  }, [displayUser]);

  useEffect(() => {
    window.addEventListener('communityEventsRefresh', refreshBlockingPendingEvent);
    return () => window.removeEventListener('communityEventsRefresh', refreshBlockingPendingEvent);
  }, [displayUser]);

  // Load public activity counts
  useEffect(() => {
    if (!displayUser) return;

    const loadPublicActivityCounts = async () => {
      try {
        const events = await eventsAPI.getUserPastEvents(displayUser.id);

        // EventType values: 2=Art, 3=Music, 4=Sport
        let artCount = 0;
        let sportCount = 0;
        let artLastMonth = 0;
        let sportLastMonth = 0;

        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

        events.forEach(event => {
          const eventDate = parseUTCDate(event.dateTime);
          const isArt = event.eventType === 2 || event.eventType === 3;
          const isSport = event.eventType === 4;

          if (isArt) {
            artCount++;
            if (eventDate >= lastMonth) {
              artLastMonth++;
            }
          }

          if (isSport) {
            sportCount++;
            if (eventDate >= lastMonth) {
              sportLastMonth++;
            }
          }
        });

        setPublicActivityArtCount(artCount);
        setPublicActivitySportCount(sportCount);
        setPublicActivityArtLastMonth(artLastMonth);
        setPublicActivitySportLastMonth(sportLastMonth);
      } catch (error) {
        console.error('Error loading public activity counts:', error);
      }
    };

    loadPublicActivityCounts();
  }, [displayUser]);

  // Implemented in main page
  // useEffect(() => {
  //   if (!displayUser) return;
  //   const loadPendingApprovalCount = async () => {
  //     try {
  //       if (!displayUser.homeLatitude || !displayUser.homeLongitude) {
  //         setPendingApprovalCount(0);
  //         return;
  //       }
  //       const pendingEvents = await communityEventsAPI.getPendingApprovalEvents(
  //         displayUser.id,
  //         displayUser.homeLatitude,
  //         displayUser.homeLongitude,
  //         displayUser.preferredRadiusKm || 10
  //       );
  //       setPendingApprovalCount(pendingEvents.length);
  //     } catch (error) {
  //       console.error('Error loading pending approval count:', error);
  //       setPendingApprovalCount(0);
  //     }
  //   };
  //   loadPendingApprovalCount();
  // }, [displayUser, pendingRefreshTrigger]);

  // useEffect(() => {
  //   const handleRefresh = () => setPendingRefreshTrigger(prev => prev + 1);
  //   window.addEventListener('pendingCountRefresh', handleRefresh);
  //   return () => window.removeEventListener('pendingCountRefresh', handleRefresh);
  // }, []);

  // Listen for storage events (when localStorage is updated in other components)
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setDisplayUser(parsedUser);
        }
      } catch (error) {
        console.error('Error reading user from localStorage:', error);
      }
    };

    // Custom event listener for same-window localStorage updates
    window.addEventListener('userUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('userUpdated', handleStorageChange);
    };
  }, []);

  return (
    <div className="top-panel">
      <div className="top-panel-main">
        <Link to="/" className="home-link">
          <h1>Cactus #Norms <span className="beta-badge">beta</span></h1>
        </Link>
        <button
          className="create-icon"
          onClick={() => setShowCreateCommunityEvent(true)}
          disabled={hasBlockingPendingEvent || !displayUser?.verified}
          title={!displayUser?.verified ? t('topPanel.notVerifiedTitle') : hasBlockingPendingEvent ? t('topPanel.pendingUnapprovedTitle') : t('topPanel.createCommunityEventTitle')}
        >
          +
        </button>
        <select
          className="language-toggle"
          value={i18n.language}
          onChange={(e) => {
            const lang = e.target.value;
            i18n.changeLanguage(lang);
            if (currentUser?.id) {
              usersAPI.updateLanguage(currentUser.id, lang).catch(() => {});
              const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
              if (storedUser) localStorage.setItem('user', JSON.stringify({ ...storedUser, language: lang }));
            }
          }}
        >
          <option value="en">EN</option>
          <option value="he">עב</option>
          <option value="bg">БГ</option>
        </select>
        {/* Implemented in main page
        <Link to="/pending-approval" className="pending-icon" title="Pending Approval">
          ⏳
          {pendingApprovalCount > 0 && (
            <span className="notification-badge">{pendingApprovalCount}</span>
          )}
        </Link>
        <Link to="/community-work" className="globe-icon" title="Discover Community Work">
          🌍
        </Link>
        */}
        {displayUser && (
          <Link to="/profile" className="profile-button">
            <UserAvatar
              user={{
                ...displayUser,
                publicActivityArtCount,
                publicActivityArtLastMonth,
                publicActivitySportCount,
                publicActivitySportLastMonth
              }}
              size="small"
              showName={false}
            />
            <span className="profile-name">{displayUser.displayName}</span>
          </Link>
        )}
      </div>

      <div className="privacy-bar">
        <Link to="/privacy" className="privacy-bar-link">{t('common.privacyPolicy')}</Link>
        <span className="privacy-bar-sep">|</span>
        <Link to="/terms" className="privacy-bar-link">{t('common.termsOfUse')}</Link>
        <span className="privacy-bar-sep">|</span>
        <Link to="/legal" className="privacy-bar-link">{t('common.legalNotice')}</Link>
      </div>

      {/* Create Community Event Dialog */}
      {showCreateCommunityEvent && displayUser && (
        <div className="create-event-dialog-overlay" onClick={() => setShowCreateCommunityEvent(false)}>
          <div className="create-event-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="close-dialog" onClick={() => setShowCreateCommunityEvent(false)}>×</button>
            <CreateCommunityEventPanel
              currentUser={displayUser}
              onEventCreated={() => {
                setShowCreateCommunityEvent(false);
                refreshBlockingPendingEvent();
                window.dispatchEvent(new CustomEvent('myGatheringsRefresh'));
                window.dispatchEvent(new CustomEvent('communityEventsRefresh'));
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TopPanel;
